/* eslint-disable @typescript-eslint/no-unused-vars */
import { useMediaQuery } from "@mui/material";
import { useGoogleAnalyticsContext, useWebStoreContext } from "contexts";
import { usePopupsState } from "hooks";
import { useImagePath } from "hooks/useImagePath";
import { IFriendReferral } from "interfaces";
import { FeatureType } from "interfaces/feature-tracker";
import { Subtitle } from "pages/FriendCenter/FriendShop/styles";
import { friendCenterAssets } from "assets/cdn";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
	useGetFeatureTrackerQuery,
	useGetFriendReferralQualifiedQuery
} from "redux/apis";
import RewardImage from "./RewardImage";
import {
	BorderLinearProgress,
	Button,
	MeterBox,
	MeterContainer,
	ProgressMeter,
	RewardStick,
	RewardTrack,
	UserImage,
	VisibleRewardsTrack
} from "./styles";

const VISIBLE_REWARD_COUNT = 5;
const FIRST_PROGRESS_STEP_DELAY_MS = 50;
const PROGRESS_STEP_DELAY_MS = 430;
let hasPlayedInitialProgressAnimation = false;

const InviteProgress = () => {
	const { getImage } = useImagePath("cdn");
	const userBarPlaceholder = getImage(
		friendCenterAssets.friendInvite.userBarPlaceholder
	);
	const navigate = useNavigate();
	const hasReportedUILaunch = useRef(false);
	const { isAnyPopupOpen } = usePopupsState();
	const { authToken, profileImage } = useWebStoreContext();
	const { reportPlaygamiEvent } = useGoogleAnalyticsContext();
	const isPortrait = useMediaQuery(
		"(orientation: portrait) and (max-width: 899px)"
	);
	const { data }: { data?: IFriendReferral } = useGetFeatureTrackerQuery(
		{
			FeatureType: FeatureType.FRIEND_REFERRAL,
			isAuthenticated: !!authToken?.value
		},
		{
			skip:
				window.location.pathname !== "/friend-center/invite/progress" ||
				document.hidden ||
				isAnyPopupOpen,
			pollingInterval: 1000 * 60,
			refetchOnFocus: true
		}
	);
	// preload qualified friends list
	const { data: friendsListData } = useGetFriendReferralQualifiedQuery(
		undefined,
		{
			skip:
				window.location.pathname !== "/friend-center/invite/progress" ||
				document.hidden,
			pollingInterval: 1000 * 60,
			refetchOnFocus: true,
			refetchOnMountOrArgChange: false
		}
	);

	const tierRewards = useMemo(() => {
		return data?.tracker?.tierRewards || [];
	}, [data?.tracker?.tierRewards]);

	const rewardTierIndex = Number(data?.tracker?.rewardTierIndex) || 0;
	const maxPage = Math.max(
		0,
		Math.ceil(tierRewards.length / VISIBLE_REWARD_COUNT) - 1
	);
	const page = Math.min(
		Math.floor(rewardTierIndex / VISIBLE_REWARD_COUNT),
		maxPage
	);
	const offset = page * VISIBLE_REWARD_COUNT;
	const visibleRewards = useMemo(() => {
		return tierRewards.slice(offset, offset + VISIBLE_REWARD_COUNT);
	}, [tierRewards, offset]);
	const visibleCompletedCount = Math.min(
		Math.max(rewardTierIndex - offset, 0),
		visibleRewards.length
	);
	const [progressAnimation, setProgressAnimation] = useState({
		offset: 0,
		completedCount: data?.tracker?.rewardTierIndex || 0
	});
	const { offset: animationOffset, completedCount: animatedCompletedCount } =
		progressAnimation;
	const visibleAnimatedCompletedCount =
		animationOffset === offset ? animatedCompletedCount : 0;
	const visibleProgressValue = visibleRewards.length
		? (visibleAnimatedCompletedCount / visibleRewards.length) * 100
		: 0;

	useEffect(() => {
		if (hasReportedUILaunch.current || !data?.tracker) {
			return;
		}

		hasReportedUILaunch.current = true;
		reportPlaygamiEvent("tp.web_frontend", {
			event_name: "web_friend_referral_ui_launch",
			event_type: "friend_referral_ui",
			action: "display",
			source: "web_friend_referral",
			progress_status: rewardTierIndex.toString(),
			progress_goal: data?.tracker?.tierRewards?.length?.toString()
		});
	}, [data?.tracker]);

	useEffect(() => {
		if (!visibleRewards.length) {
			return;
		}

		if (hasPlayedInitialProgressAnimation) {
			if (
				animationOffset !== offset ||
				animatedCompletedCount !== visibleCompletedCount
			) {
				setProgressAnimation({
					offset,
					completedCount: visibleCompletedCount
				});
			}

			return;
		}

		if (animationOffset !== offset) {
			setProgressAnimation({
				offset,
				completedCount: 0
			});
			return;
		}

		if (animatedCompletedCount > visibleCompletedCount) {
			setProgressAnimation({
				offset,
				completedCount: visibleCompletedCount
			});
			return;
		}

		if (animatedCompletedCount >= visibleCompletedCount) {
			hasPlayedInitialProgressAnimation = true;
			return;
		}

		const timeout = window.setTimeout(
			() => {
				setProgressAnimation((current) => {
					if (current.offset !== offset) {
						return current;
					}

					return {
						offset,
						completedCount: Math.min(
							current.completedCount + 1,
							visibleCompletedCount
						)
					};
				});
			},
			animatedCompletedCount === 0
				? FIRST_PROGRESS_STEP_DELAY_MS
				: PROGRESS_STEP_DELAY_MS
		);

		return () => window.clearTimeout(timeout);
	}, [
		animatedCompletedCount,
		animationOffset,
		offset,
		visibleCompletedCount,
		visibleRewards.length
	]);

	const handleSelectFriend = () => {
		reportPlaygamiEvent("tp.web_frontend", {
			event_name: "friend_invite_button_click",
			event_type: "friend_referral_ui",
			action: "invite_button_click",
			source: "web_friend_referral"
		});
		navigate("friends");
	};

	return (
		<>
			<Subtitle className="invite">
				Get Rewards for each person that joins the web!
			</Subtitle>
			{isPortrait && (
				<Button onClick={() => handleSelectFriend()}>Invite</Button>
			)}
			<MeterContainer>
				<UserImage
					src={profileImage || userBarPlaceholder}
					style={{ zIndex: tierRewards.length + 1 }}
				/>
				<VisibleRewardsTrack $rewardCount={visibleRewards.length}>
					<ProgressMeter
						$rewardCount={visibleRewards.length}
						className={
							visibleRewards.length > 0 &&
							visibleAnimatedCompletedCount >= visibleRewards.length
								? "completed"
								: ""
						}
					>
						<BorderLinearProgress
							variant="determinate"
							value={visibleProgressValue}
						/>
					</ProgressMeter>
					<RewardTrack>
						{visibleRewards.map((reward, i) => {
							const index = offset + i;
							const completed = i < visibleAnimatedCompletedCount;
							return (
								<MeterBox
									key={index}
									className={completed ? "completed" : ""}
									style={{ zIndex: tierRewards.length - index }}
								>
									<p className={completed ? "completed" : ""}>{index + 1}</p>
									<RewardImage completed={completed} reward={reward} />
									<RewardStick className={completed ? "completed" : ""} />
								</MeterBox>
							);
						})}
					</RewardTrack>
				</VisibleRewardsTrack>
			</MeterContainer>
			{!isPortrait && (
				<Button onClick={() => handleSelectFriend()}>Invite</Button>
			)}
		</>
	);
};

export default InviteProgress;
